// Fixed ReportSectionsView.js with proper layout and visibility
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  FileText,
  Lock,
  Unlock,
  Download,
  Loader,
  ChevronRight,
  Edit,
  CreditCard,
  CheckCircle,
  AlertTriangle,
  Save,
  X,
  Coins,
} from "lucide-react";
import axiosInstance from "./axiosConfig";
import ReactMarkdown from "react-markdown";
import { toast } from "react-hot-toast";

const ReportSectionsView = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeSection, setActiveSection] = useState(null);
  const [sectionLoading, setSectionLoading] = useState(false);
  const [sectionContent, setSectionContent] = useState(null);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState({ markdown: "", summary: "" });
  const [pdfExporting, setPdfExporting] = useState(false);
  const [docxExporting, setDocxExporting] = useState(false);
  const [tocLoading, setTocLoading] = useState(true);
  const [userCredits, setUserCredits] = useState(null);
  const [purchaseLoading, setPurchaseLoading] = useState(false);

  const noCopyStyle = {
    userSelect: "none",
    WebkitUserSelect: "none",
    MozUserSelect: "none",
    msUserSelect: "none",
    WebkitTouchCallout: "none",
  };

  // 2. Add copy prevention event handler
  useEffect(() => {
    if (report?.is_preview && !report?.is_purchased) {
      const preventCopy = (e) => {
        e.preventDefault();
        // Show toast notification when copy is attempted
        // toast.error("Copying is not allowed in preview mode. Purchase the full report for complete access.");
        alert("This action is not allowed");
        return false;
      };

      document.addEventListener("copy", preventCopy);

      return () => {
        document.removeEventListener("copy", preventCopy);
      };
    }
  }, [report]);

  // Get report data
  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        setTocLoading(true);
        const response = await axiosInstance.get(`reports/${reportId}/`);
        setReport(response.data);

        // Set first section as active by default
        if (response.data.sections && response.data.sections.length > 0) {
          const firstSection = response.data.sections[0];
          setActiveSection(firstSection);

          // Only fetch content if it's unlocked or report purchased
          if (!isSectionLocked(firstSection, response.data)) {
            await fetchSectionContent(firstSection.id);
          } else {
            setSectionContent(null);
          }
        }

        // Fetch user's credits
        fetchUserCredits();
      } catch (err) {
        console.error("Error fetching report:", err);
        setError("Failed to load the business plan. Please try again.");
      } finally {
        setLoading(false);
        setTocLoading(false);
      }
    };

    if (reportId) {
      fetchReport();
    }
  }, [reportId]);

  // Fetch user's credits
  const fetchUserCredits = async () => {
    try {
      const response = await axiosInstance.get("get-user-credits/");
      setUserCredits(response.data);
    } catch (err) {
      console.error("Error fetching user credits:", err);
    }
  };

  // Fetch section content
  const fetchSectionContent = async (sectionId) => {
    // Don't fetch if editing
    if (isEditing) return;

    try {
      setSectionLoading(true);
      const response = await axiosInstance.get(`sections/${sectionId}/`);
      setSectionContent(response.data);

      // Reset edit state when fetching new content
      setIsEditing(false);
      setEditContent({ markdown: "", summary: "" });
    } catch (err) {
      console.error("Error fetching section content:", err);
      setSectionContent(null);
      toast.error("Failed to load section content.");
    } finally {
      setSectionLoading(false);
    }
  };

  // Purchase report with credit
  const purchaseWithCredit = async () => {
    try {
      setPurchaseLoading(true);
      const response = await axiosInstance.post(`reports/${reportId}/`, {
        action: "purchase_with_credit",
      });

      if (response.data.success) {
        toast.success("Report unlocked successfully!");
        // Update report state
        setReport((prev) => ({
          ...prev,
          is_purchased: true,
          is_preview: false,
          sections: prev.sections.map((section) => ({
            ...section,
            is_locked: false,
          })),
        }));

        // Refresh user's credits
        fetchUserCredits();
      }
    } catch (err) {
      console.error("Error purchasing report:", err);
      toast.error(err.response?.data?.message || "Failed to unlock report");
    } finally {
      setPurchaseLoading(false);
    }
  };

  // Generate section content
  const generateSectionContent = async (useCredit = false) => {
    if (!activeSection) return;

    try {
      setGenerateLoading(true);
      const response = await axiosInstance.post(`sections/${activeSection.id}/generate/`, {
        purchase_with_credit: useCredit,
      });

      // Update the content
      setSectionContent(response.data);

      // Update the active section's generated status in the main report state
      setReport((prevReport) => {
        const updatedSections = prevReport.sections.map((section) =>
          section.id === activeSection.id ? { ...section, is_generated: true } : section
        );
        return { ...prevReport, sections: updatedSections };
      });

      // Also update the activeSection state directly
      setActiveSection((prevSection) => ({
        ...prevSection,
        is_generated: true,
      }));

      // Reset edit state
      setIsEditing(false);
      toast.success("Section content generated!");

      // If credit was used, refresh user's credits
      if (useCredit) {
        fetchUserCredits();
      }
    } catch (err) {
      console.error("Error generating content:", err);

      // Check if error is due to section being locked
      if (err.response?.data?.requires_purchase) {
        // Show option to use credit
        if (err.response?.data?.can_use_credits) {
          if (window.confirm("This section is locked. Would you like to use 1 report credit to unlock the full report?")) {
            generateSectionContent(true);
            return;
          }
        } else {
          toast.error("This section is locked. Please purchase the full report.");
        }
      } else {
        toast.error("Failed to generate content.");
      }
    } finally {
      setGenerateLoading(false);
    }
  };

  // Toggle edit mode
  const toggleEditMode = () => {
    if (!isEditing) {
      // Entering edit mode: Initialize editContent with current section content
      setEditContent({
        markdown: sectionContent?.content?.markdown || "",
        summary: sectionContent?.content?.summary || "",
      });
    } else {
      // Exiting edit mode (Cancel): Reset editContent, no save needed
      setEditContent({ markdown: "", summary: "" });
    }
    setIsEditing(!isEditing);
  };

  // Save edited content
  const handleSaveContent = async () => {
    if (!activeSection || !isEditing) return;

    try {
      setSectionLoading(true);
      await axiosInstance.put(`sections/${activeSection.id}/update/`, {
        content: editContent,
      });

      // Update the local sectionContent state with the saved data
      setSectionContent((prev) => ({
        ...prev,
        content: {
          ...prev.content,
          markdown: editContent.markdown,
          summary: editContent.summary,
        },
      }));

      // Exit edit mode
      setIsEditing(false);
      toast.success("Content updated successfully");
    } catch (err) {
      console.error("Error updating content:", err);
      toast.error("Failed to update content");
    } finally {
      setSectionLoading(false);
    }
  };

  // Handle section click
  const handleSectionClick = async (section) => {
    // Prevent clicking if already active or if locked
    if (activeSection?.id === section.id || isSectionLocked(section, report)) {
      return;
    }

    // If currently editing, prompt user or discard changes before switching
    if (isEditing) {
      if (!window.confirm("You have unsaved changes. Are you sure you want to switch sections? Changes will be lost.")) {
        return;
      }
      // If confirmed, exit edit mode without saving
      setIsEditing(false);
      setEditContent({ markdown: "", summary: "" });
    }

    setActiveSection(section);

    // Fetch content for the newly selected section
    await fetchSectionContent(section.id);
  };

  // Export report
  const handleExport = async (format) => {
    // Set the loading state for the corresponding format
    if (format === "pdf") {
      setPdfExporting(true);
    } else if (format === "docx") {
      setDocxExporting(true);
    }

    try {
      const response = await axiosInstance.get(`reports/${reportId}/export/${format}/`, { responseType: "blob" });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;

      // Use report title for filename, fallback if needed
      const filename = report?.title ? `${report.title}.${format}` : `report.${format}`;

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url); // Clean up blob URL

      toast.success(`Report exported as ${format.toUpperCase()}!`);
    } catch (err) {
      console.error(`Error exporting as ${format}:`, err);
      toast.error(`Error exporting as ${format.toUpperCase()}.`);
    } finally {
      // Reset loading state
      if (format === "pdf") {
        setPdfExporting(false);
      } else if (format === "docx") {
        setDocxExporting(false);
      }
    }
  };

  // Determine if a section is locked
  const isSectionLocked = (section, currentReport) => {
    // Ensure section and currentReport are valid
    if (!section || !currentReport) return true;

    // First section is always unlocked
    if (section.section_number === 1) return false;

    // If report is purchased, all sections are unlocked
    if (currentReport.is_purchased) return false;

    // Otherwise it's locked in preview mode
    return currentReport.is_preview === true;
  };

  // --- Render Logic ---
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <Loader className="h-12 w-12 text-green-600 animate-spin mb-4" />
        <span className="text-lg font-medium text-gray-700">Loading Business Plan...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex flex-col items-center justify-center text-center">
        <AlertTriangle className="h-16 w-16 text-red-500 mb-4" />
        <div className="text-red-500 text-xl font-medium mb-4">{error}</div>
        <button
          onClick={() => navigate("/dashboard/")}
          className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Dashboard
        </button>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex flex-col items-center justify-center text-center">
        <FileText className="h-16 w-16 text-gray-400 mb-4" />
        <div className="text-gray-500 text-xl font-medium mb-4">Business Plan not found or access denied.</div>
        <button
          onClick={() => navigate("/")}
          className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Dashboard
        </button>
      </div>
    );
  }

  // --- Main Component Structure ---
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center min-w-0">
            <button onClick={() => navigate("/dynamicreports")} className="mr-4 text-gray-500 hover:text-gray-700 flex-shrink-0">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-xl font-bold text-gray-900 truncate">{report.title || "Business Plan"}</h1>
          </div>

          <div className="flex items-center space-x-2 flex-shrink-0">
            {report.is_preview && !report.is_purchased && userCredits && userCredits.report_credits > 0 && (
              <button
                onClick={purchaseWithCredit}
                disabled={purchaseLoading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 shadow-md hover:shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 whitespace-nowrap"
              >
                {purchaseLoading ? <Loader className="h-4 w-4 mr-2 animate-spin" /> : <Coins className="h-4 w-4 mr-2" />}
                Use 1 Credit
              </button>
            )}

            {report.is_preview && !report.is_purchased && (
              <button
                onClick={() => navigate("/credits/")}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 shadow-md hover:shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 whitespace-nowrap"
              >
                <CreditCard className="h-4 w-4 mr-2" />
                Unlock Full Report
              </button>
            )}

            <button
              onClick={() => handleExport("pdf")}
              disabled={pdfExporting}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-70 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {pdfExporting ? (
                <>
                  <Loader className="h-4 w-4 mr-1 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-1" />
                  PDF
                </>
              )}
            </button>
            <button
              onClick={() => handleExport("docx")}
              disabled={docxExporting}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-70 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {docxExporting ? (
                <>
                  <Loader className="h-4 w-4 mr-1 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-1" />
                  DOCX
                </>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Premium Banner - Positioned above the content areas on ALL screen sizes */}
      {report.is_preview && !report.is_purchased && (
        <div className="bg-gradient-to-r from-green-600 to-green-500 text-white py-3 px-4 sm:px-6 shadow-md w-full z-5">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-start sm:items-center">
              <Lock className="h-6 w-6 mr-3 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-lg">Preview Mode</h3>
                <p className="text-green-50 text-sm">
                  Only the Executive Summary is fully available. Unlock all sections for complete access.
                </p>
              </div>
            </div>
            <div className="flex space-x-2 w-full sm:w-auto justify-center">
              {userCredits && userCredits.report_credits > 0 ? (
                <button
                  onClick={purchaseWithCredit}
                  disabled={purchaseLoading}
                  className="px-4 py-2 bg-white text-green-700 rounded-md font-medium text-sm whitespace-nowrap hover:bg-green-50 transition-colors duration-200 shadow-sm flex items-center"
                >
                  {purchaseLoading ? <Loader className="h-4 w-4 mr-2 animate-spin" /> : <Coins className="h-4 w-4 mr-2" />}
                  Use 1 Credit
                </button>
              ) : (
                <button
                  onClick={() => navigate("/credits/")}
                  className="px-4 py-2 bg-white text-green-700 rounded-md font-medium text-sm whitespace-nowrap hover:bg-green-50 transition-colors duration-200 shadow-sm"
                >
                  Buy Full Access
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Content Container */}
      <div className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Two-Column Layout Container */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left sidebar - Table of contents */}
          <aside className="w-full lg:w-1/4 lg:flex-shrink-0">
            <div className="bg-white rounded-lg shadow overflow-hidden sticky top-[calc(3.5rem+1rem)]">
              <div className="px-4 py-5 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-lg font-medium text-gray-900">Table of Contents</h2>
                {tocLoading && <Loader className="h-4 w-4 text-green-500 animate-spin" />}
              </div>

              {tocLoading ? (
                <div className="flex items-center justify-center p-8">
                  <Loader className="h-8 w-8 text-green-500 animate-spin" />
                </div>
              ) : (
                <nav className="divide-y divide-gray-200 max-h-[calc(100vh-12rem)] overflow-y-auto">
                  {report.sections && report.sections.length > 0 ? (
                    report.sections.map((section) => {
                      const locked = isSectionLocked(section, report);
                      const isActive = activeSection?.id === section.id;
                      return (
                        <button
                          key={section.id}
                          onClick={() => handleSectionClick(section)}
                          disabled={locked}
                          className={`w-full text-left px-4 py-3 flex items-center transition-all duration-200 ${
                            isActive
                              ? "bg-green-50 text-green-700 font-semibold border-l-4 border-green-500"
                              : "hover:bg-gray-50 text-gray-900 border-l-4 border-transparent"
                          } ${locked ? "cursor-not-allowed opacity-60 bg-gray-50" : "cursor-pointer"}`}
                        >
                          <span className={`mr-3 text-sm font-medium ${isActive ? "text-green-600" : "text-gray-500"}`}>
                            {section.section_number}.
                          </span>
                          <span className="flex-1 truncate font-medium">{section.title}</span>
                          {locked ? (
                            <Lock className="ml-2 h-4 w-4 text-gray-400 flex-shrink-0" />
                          ) : section.is_generated ? (
                            <CheckCircle className="ml-2 h-4 w-4 text-green-500 flex-shrink-0" />
                          ) : (
                            <ChevronRight className="ml-2 h-4 w-4 text-gray-400 flex-shrink-0" />
                          )}
                        </button>
                      );
                    })
                  ) : (
                    <p className="p-4 text-gray-500">No sections found.</p>
                  )}
                </nav>
              )}
            </div>

            {/* Sidebar upgrade card - keep for additional visibility on larger screens */}
            {report.is_preview && !report.is_purchased && (
              <div className="mt-6 bg-gradient-to-br from-green-100 to-green-50 rounded-lg p-5 border border-green-200 shadow-sm hidden lg:block">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <Lock className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-green-800">Premium Content</h3>
                    <div className="mt-2 text-sm text-green-700 space-y-1">
                      <p className="mb-3">Only the Executive Summary is available. Unlock to access all sections and:</p>
                      <ul className="space-y-2">
                        <li className="flex items-start">
                          <CheckCircle className="h-4 w-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                          <span>Market analysis</span>
                        </li>
                        <li className="flex items-start">
                          <CheckCircle className="h-4 w-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                          <span>Financial projections</span>
                        </li>
                        <li className="flex items-start">
                          <CheckCircle className="h-4 w-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                          <span>Export as PDF/Word</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </aside>

          {/* Main content area - Section content */}
          <main className="flex-1 bg-white rounded-lg shadow overflow-hidden">
            {/* Section Loading State */}
            {sectionLoading ? (
              <div className="flex flex-col items-center justify-center h-96">
                <Loader className="h-10 w-10 text-green-600 animate-spin mb-4" />
                <span className="text-gray-600 font-medium">Loading Section Content...</span>
              </div>
            ) : /* No Active Section State */
            !activeSection ? (
              <div className="flex flex-col items-center justify-center text-center p-12 h-96">
                <FileText className="h-16 w-16 text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-1">Select a Section</h3>
                <p className="text-sm text-gray-500">Choose a section from the Table of Contents to view its content.</p>
              </div>
            ) : /* Active Section Locked State */
            isSectionLocked(activeSection, report) ? (
              <div className="flex flex-col items-center justify-center text-center p-12 h-96">
                <Lock className="h-16 w-16 text-green-300 mb-6" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Premium Content</h3>
                <p className="text-base text-gray-600 mb-6 max-w-md">
                  This section is only available in the full report. Unlock now to access all business plan sections.
                </p>

                {userCredits && userCredits.report_credits > 0 ? (
                  <button
                    onClick={purchaseWithCredit}
                    disabled={purchaseLoading}
                    className="inline-flex items-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 shadow-md hover:shadow-lg transition-all duration-200 mb-2"
                  >
                    {purchaseLoading ? (
                      <>
                        <Loader className="h-5 w-5 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Coins className="h-5 w-5 mr-2" />
                        Use 1 Report Credit
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={() => navigate("/credits/")}
                    className="inline-flex items-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 shadow-md hover:shadow-lg transition-all duration-200"
                  >
                    <CreditCard className="h-5 w-5 mr-2" />
                    Unlock Full Report
                  </button>
                )}
              </div>
            ) : (
              /* Active Section Content Display */
              <div className="p-6 h-full flex flex-col">
                {/* Content header with edit/generate buttons */}
                <div className="flex justify-between items-start mb-6 flex-shrink-0 sticky top-0 bg-white z-10 pb-3">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {activeSection.section_number}. {activeSection.title}
                  </h2>
                  {/* Only show buttons for purchased reports or non-preview reports */}
                  {(!report.is_preview || report.is_purchased) &&
                    (sectionContent?.is_generated ? (
                      <button
                        onClick={toggleEditMode}
                        className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm leading-5 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 shadow-sm transition-colors duration-200"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        {isEditing ? "Cancel Edit" : "Edit"}
                      </button>
                    ) : (
                      <button
                        onClick={() => generateSectionContent()}
                        disabled={generateLoading}
                        className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 shadow-sm transition-colors duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
                      >
                        {generateLoading ? (
                          <>
                            <Loader className="animate-spin h-4 w-4 mr-1" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4 w-4 mr-1"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                              />
                            </svg>
                            Generate
                          </>
                        )}
                      </button>
                    ))}
                </div>
                {/* Content display area */}
                <div className="flex-1 overflow-y-auto">
                  {/* Editing Interface */}
                  {isEditing ? (
                    <div className="mb-4 relative">
                      {/* Sticky edit controls at the top */}
                      <div className="sticky top-[calc(3.5rem+1rem)] z-10 bg-white pb-4 flex justify-end space-x-2 mb-2 border-b border-gray-200">
                        <button
                          onClick={toggleEditMode}
                          type="button"
                          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
                        >
                          <X className="h-4 w-4 mr-2" />
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveContent}
                          type="button"
                          disabled={sectionLoading}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-70 disabled:cursor-not-allowed transition-colors duration-200"
                        >
                          {sectionLoading ? (
                            <>
                              <Loader className="animate-spin h-4 w-4 mr-2" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save className="h-4 w-4 mr-2" />
                              Save Changes
                            </>
                          )}
                        </button>
                      </div>

                      <div className="mb-4">
                        <label htmlFor="markdown-content" className="block text-sm font-medium text-gray-700 mb-1">
                          Markdown Content
                        </label>
                        <textarea
                          id="markdown-content"
                          rows={20}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm font-mono p-3"
                          value={editContent.markdown || ""}
                          onChange={(e) => setEditContent({ ...editContent, markdown: e.target.value })}
                        />
                      </div>
                      <div className="mb-4">
                        <label htmlFor="summary-content" className="block text-sm font-medium text-gray-700 mb-1">
                          Summary (Optional)
                        </label>
                        <textarea
                          id="summary-content"
                          rows={4}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm p-3"
                          value={editContent.summary || ""}
                          onChange={(e) => setEditContent({ ...editContent, summary: e.target.value })}
                        />
                        <p className="mt-1 text-xs text-gray-500">A brief summary of this section.</p>
                      </div>
                    </div>
                  ) : /* Regular Content Display */
                  !sectionContent ? (
                    <div className="text-center py-10 text-gray-500">
                      <p>Loading content or content unavailable.</p>
                    </div>
                  ) : !sectionContent.is_generated ? (
                    // Content not generated yet placeholder
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center flex flex-col items-center justify-center h-full">
                      <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Content Not Generated Yet</h3>
                      <p className="text-sm text-gray-500 mb-6">Click the "Generate" button to create content for this section.</p>
                      <button
                        onClick={() => generateSectionContent()}
                        disabled={generateLoading}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 shadow-sm transition-colors duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
                      >
                        {generateLoading ? (
                          <>
                            <Loader className="animate-spin h-4 w-4 mr-2" />
                            Generating Content...
                          </>
                        ) : (
                          "Generate Content"
                        )}
                      </button>
                    </div>
                  ) : (
                    // Actual Content Display (Markdown, Diagrams, Citations)
                    <div className="prose prose-green max-w-none">
                      {/* Render Markdown */}
                      <div
                        className="prose prose-green max-w-none"
                        style={report.is_preview && !report.is_purchased ? noCopyStyle : {}}
                        onCopy={report.is_preview && !report.is_purchased ? (e) => e.preventDefault() : undefined}
                      >
                        <ReactMarkdown>{sectionContent.content?.markdown || "*No content available.*"}</ReactMarkdown>
                      </div>

                      {/* Render Diagrams */}
                      {sectionContent.diagrams && sectionContent.diagrams.length > 0 && (
                        <div className="mt-8 pt-6 border-t border-gray-200">
                          <h3 className="text-xl font-semibold mb-4 not-prose">Diagrams</h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {sectionContent.diagrams.map((diagram) => (
                              <div
                                key={diagram.id}
                                className="mb-6 not-prose bg-white p-4 border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200"
                              >
                                <h4 className="font-medium mb-2 text-lg">{diagram.title || "Diagram"}</h4>
                                <div
                                  className="overflow-x-auto"
                                  dangerouslySetInnerHTML={{
                                    __html: diagram.svg_content || "<p>Error loading diagram</p>",
                                  }}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Render Citations */}
                      {sectionContent.citations && sectionContent.citations.length > 0 && (
                        <div className="mt-8 border-t border-gray-200 pt-6">
                          <h3 className="text-xl font-semibold mb-4 not-prose">References</h3>
                          <ul className="list-disc pl-5 space-y-3">
                            {sectionContent.citations.map((citation) => (
                              <li key={citation.id} className="text-sm">
                                {citation.title && <strong>{citation.title}.</strong>}
                                {citation.url && (
                                  <span className="ml-1">
                                    Available at:{" "}
                                    <a
                                      href={citation.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-green-600 hover:underline break-all"
                                    >
                                      {citation.url}
                                    </a>
                                  </span>
                                )}
                                {citation.authors && <p className="text-xs text-gray-600 mt-1">Authors: {citation.authors}</p>}
                                {citation.description && <p className="text-xs text-gray-600 mt-1 italic">{citation.description}</p>}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default ReportSectionsView;
